import React, { useState, useRef } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY, DEFAULT_LOCATION, PLACE_TYPE_CATEGORIES } from '../constants/placeTypes';
import { TARGET_GROUP_PRESETS, formatMeters, targetLabel } from '../constants/walkabilityPresets';
import WalkingMap from './WalkingMap';
import PlaceTypeSelector from './PlaceTypeSelector';
import TopBar from './TopBar';

const libraries = ['places', 'geometry'];

const STATUS_TEXT = {
  ok: 'Finns',
  far: 'Finns, men längre bort',
  missing: 'Saknas',
  error: 'Sökningen misslyckades'
};

const WalkingDistanceCalculator = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  const [isPlaceMarkerMode, setIsPlaceMarkerMode] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [originMarker, setOriginMarker] = useState(null);
  const [markerAddress] = useState('Ingen position vald');
  const [status, setStatus] = useState('Väntar på att kartan skall laddas...');
  const [typeCounts, setTypeCounts] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [statusIsError, setStatusIsError] = useState(false);

  // Målgruppslistor: vald lista, vilka behov som är ikryssade, och utfallet
  // per behov från senaste kontrollen (kvar tills nästa körning).
  const [activePresetId, setActivePresetId] = useState(null);
  const [checkedNeeds, setCheckedNeeds] = useState({});
  const [presetResults, setPresetResults] = useState(null);
  const [needListOpen, setNeedListOpen] = useState(false);

  const walkingMapRef = useRef(null);

  const activePreset = TARGET_GROUP_PRESETS.find((p) => p.id === activePresetId) || null;

  const handleMarkerPlaced = (location) => {
    setOriginMarker(location);
    setIsPlaceMarkerMode(false);
    setStatusIsError(false);
    setPresetResults(null);
    setTypeCounts({});
    setStatus('Startpunkt satt. Välj en målgrupp eller platstyper och sök.');
  };

  const handleMapLoad = () => {
    setStatus('Kartan är laddad (Lilla Essingen, Stockholm). Klicka på "Sätt markör mode" för att välja startpunkt.');
  };

  const togglePlaceMarkerMode = () => {
    setIsPlaceMarkerMode(!isPlaceMarkerMode);
    if (!isPlaceMarkerMode) {
      setStatus('Klicka var som helst på kartan för att placera startpunkten');
    } else {
      setStatus('Markörläget är avstängt');
    }
  };

  const choosePreset = (presetId) => {
    if (presetId === activePresetId) return;
    const preset = TARGET_GROUP_PRESETS.find((p) => p.id === presetId);
    setActivePresetId(presetId);
    setPresetResults(null);
    setNeedListOpen(false);
    const checked = {};
    preset.needs.forEach((need) => { checked[need.id] = true; });
    setCheckedNeeds(checked);
  };

  const toggleNeed = (needId) => {
    setCheckedNeeds((prev) => ({ ...prev, [needId]: !prev[needId] }));
  };

  const handlePresetSearch = async () => {
    if (!originMarker) {
      setStatusIsError(true);
      setStatus('Sätt en startpunkt på kartan först.');
      return;
    }
    const needs = activePreset.needs.filter((need) => checkedNeeds[need.id]);
    if (needs.length === 0) {
      setStatusIsError(true);
      setStatus('Kryssa i minst en typ av service att kontrollera.');
      return;
    }
    if (!window.walkingMapRef || !window.walkingMapRef.searchPreset) return;

    setIsSearching(true);
    setStatusIsError(false);
    setTypeCounts({});
    setStatus(`Kontrollerar ${needs.length} typer av service för ${activePreset.label}...`);
    try {
      const results = await window.walkingMapRef.searchPreset(needs);
      setPresetResults(results);
      const missing = results.filter((r) => r.status !== 'ok').length;
      setStatus(missing === 0
        ? `Allt finns inom riktavstånd för ${activePreset.label}.`
        : `${missing} av ${results.length} saknas inom riktavstånd för ${activePreset.label}.`);
    } catch (error) {
      console.error(error);
      setStatusIsError(true);
      setStatus('Kontrollen misslyckades. Försök igen.');
    } finally {
      setIsSearching(false);
    }
  };

  const focusPlace = (place) => {
    if (window.walkingMapRef && window.walkingMapRef.focusPlace) {
      window.walkingMapRef.focusPlace(place);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearch = () => {
    if (!originMarker) {
      setStatusIsError(true);
      setStatus('Sätt en startpunkt på kartan först.');
      return;
    }

    if (selectedTypes.length === 0) {
      // Ingen egen platstyp vald: har användaren valt en målgrupp är det den
      // som ska kontrolleras, oavsett vilken sökknapp som trycktes.
      if (activePreset) {
        handlePresetSearch();
        return;
      }
      setStatusIsError(true);
      setStatus('Välj en målgrupp ovan eller minst en platstyp i listan.');
      return;
    }

    setIsSearching(true);
    setStatusIsError(false);
    setStatus(`Söker ${selectedTypes.length} platstyp(er) inom gångavstånd...`);

    // Trigger search in WalkingMap component
    if (window.walkingMapRef && window.walkingMapRef.searchPlaces) {
      window.walkingMapRef.searchPlaces();
    }
  };

  const handleSearchComplete = (places) => {
    setIsSearching(false);

    // Calculate type counts
    const counts = {};
    const closestPlaces = {};

    // Get display names lookup
    const displayNames = {};
    PLACE_TYPE_CATEGORIES.forEach(category => {
      category.options.forEach(option => {
        displayNames[option.value] = option.label;
      });
    });

    // Count places by type
    selectedTypes.forEach(type => {
      counts[type] = 0;
      closestPlaces[type] = null;
    });

    places.forEach(place => {
      if (!place.types) return;

      selectedTypes.forEach(searchedType => {
        let isMatch = false;

        // Check if place matches this type
        if (place.types && place.types.includes(searchedType)) {
          isMatch = true;
        }

        // Special handling for Swedish types
        if (['preschool', 'primary_school', 'secondary_school', 'systembolaget'].includes(searchedType)) {
          const placeName = place.name.toLowerCase();
          if (searchedType === 'preschool' && (placeName.includes('förskola') || placeName.includes('dagis'))) {
            isMatch = true;
          } else if (searchedType === 'primary_school' && placeName.includes('grundskola')) {
            isMatch = true;
          } else if (searchedType === 'secondary_school' && (placeName.includes('gymnasie') || placeName.includes('gymnasium'))) {
            isMatch = true;
          } else if (searchedType === 'systembolaget' && placeName.includes('systembolaget')) {
            isMatch = true;
          }
        }

        if (isMatch) {
          counts[searchedType]++;

          if (place.walkingDistanceMeters !== undefined) {
            if (!closestPlaces[searchedType] ||
                place.walkingDistanceMeters < closestPlaces[searchedType].walkingDistanceMeters) {
              closestPlaces[searchedType] = place;
            }
          }
        }
      });
    });

    // Format counts for display
    const formattedCounts = {};
    Object.keys(counts).forEach(type => {
      if (counts[type] > 0) {
        let displayText = `${displayNames[type] || type}: ${counts[type]}`;

        if (closestPlaces[type] && closestPlaces[type].walkingDistanceMeters) {
          displayText += ` (Närmaste: ${closestPlaces[type].walkingDistanceMeters} m`;
          if (closestPlaces[type].name) {
            displayText += ` - ${closestPlaces[type].name}`;
          }
          displayText += ')';
        }

        formattedCounts[type] = displayText;
      }
    });

    setPresetResults(null);
    setTypeCounts(formattedCounts);
    setStatus(`Hittade ${places.length} platser inom 10 minuters promenad.`);
  };

  const resultFor = (need) => presetResults && presetResults.find((r) => r.need.id === need.id);

  const describeResult = (r) => {
    const need = r.need;
    const unit = need.crowFlies ? 'fågelvägen' : 'gångväg';
    const metric = need.crowFlies ? 'straightLineMeters' : 'walkingDistanceMeters';
    if (r.status === 'error') return STATUS_TEXT.error;
    if (r.status === 'missing') return 'Saknas inom 10 minuters promenad';
    const nearest = r.nearest;
    const dist = `${formatMeters(nearest[metric])} ${unit}`;
    const more = r.withinTargetCount > 1 ? `, ${r.withinTargetCount} st inom riktavstånd` : '';
    return (
      <>
        {r.status === 'far' ? 'Närmaste: ' : ''}
        <button
          type="button"
          className="walk-place-link"
          onClick={() => focusPlace(nearest)}
          data-tip={`Visa gångvägen till ${nearest.name} på kartan`}
        >
          {nearest.name}
        </button>
        {`, ${dist}${more}`}
      </>
    );
  };

  const missingResults = presetResults ? presetResults.filter((r) => r.status !== 'ok') : [];

  if (loadError) {
    return <div className="error">Error loading maps: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div className="status">Laddar karta...</div>;
  }

  return (
    <>
      <TopBar />
      <div className="walking-calculator ut-page">
      <div className="ut-page-head">
        <div className="ut-eyebrow">Promenadavstånd</div>
        <h1>Vad finns på promenadavstånd?</h1>
      </div>

      <div className="map-container">
        <WalkingMap
          ref={walkingMapRef}
          center={DEFAULT_LOCATION}
          onMarkerPlaced={handleMarkerPlaced}
          isPlaceMarkerMode={isPlaceMarkerMode}
          onMapLoad={handleMapLoad}
          selectedTypes={selectedTypes}
          onSearchComplete={handleSearchComplete}
        />
      </div>

      <div className="marker-controls">
        <button
          id="placeMarkerButton"
          className={isPlaceMarkerMode ? 'active-mode' : ''}
          onClick={togglePlaceMarkerMode}
          data-tip="walk.marker"
        >
          Sätt markör mode
        </button>
        {isPlaceMarkerMode && (
          <span id="markerInstructions" style={{ marginLeft: '10px', color: '#4285f4' }}>
            <br />Klicka var som helst på kartan för att placera en markör.
          </span>
        )}
        {originMarker && (
          <div id="markerInfo" style={{ marginTop: '10px' }}>
            <strong>Adress:</strong> <span id="markerPosition">{markerAddress}</span>
          </div>
        )}
      </div>

      <section className="walk-presets">
        <h2>Service per målgrupp</h2>
        <p className="walk-presets-lead">
          Välj en målgrupp så kontrolleras den service som forskning och planeringspraxis
          säger bör finnas inom gångavstånd. Riktvärden, inte svenska krav.
        </p>
        <div className="walk-preset-buttons" role="group" aria-label="Målgrupp">
          {TARGET_GROUP_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`walk-preset-btn${preset.id === activePresetId ? ' is-active' : ''}`}
              onClick={() => choosePreset(preset.id)}
              data-tip={`walk.preset.${preset.id}`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {activePreset && (
          <div className="walk-needs">
            <p className="walk-preset-intro">{activePreset.intro}</p>

            <button
              type="button"
              className="walk-need-toggle"
              onClick={() => setNeedListOpen((open) => !open)}
              aria-expanded={needListOpen}
              data-tip="walk.need.list"
            >
              <span className="walk-need-toggle-caret" aria-hidden="true">{needListOpen ? '▾' : '▸'}</span>
              {needListOpen ? 'Dölj listan' : 'Visa listan'}
              <span className="walk-need-toggle-count">
                {activePreset.needs.filter((need) => checkedNeeds[need.id]).length} av {activePreset.needs.length} ikryssade
              </span>
            </button>

            {needListOpen && (
            <ul className="walk-need-list">
              {activePreset.needs.map((need) => {
                const r = resultFor(need);
                const cls = r ? `is-${r.status}` : '';
                return (
                  <li key={need.id} className={`walk-need ${cls}`}>
                    <label className="walk-need-main">
                      <input
                        type="checkbox"
                        checked={!!checkedNeeds[need.id]}
                        onChange={() => toggleNeed(need.id)}
                        data-tip="walk.need.toggle"
                      />
                      <span className="walk-need-label">{need.label}</span>
                      <span className="walk-need-target">{targetLabel(need)}</span>
                    </label>
                    <div className="walk-need-meta">
                      <span className="walk-need-why">{need.why}</span>
                      <span className="walk-need-source">Källa: {need.source}</span>
                    </div>
                    {r && (
                      <div className="walk-need-result">
                        <span className="walk-need-badge">{STATUS_TEXT[r.status]}</span>
                        <span>{describeResult(r)}</span>
                      </div>
                    )}
                    {need.gap && <div className="walk-need-gap">{need.gap}</div>}
                  </li>
                );
              })}
            </ul>
            )}

            <button
              type="button"
              className="walk-preset-run"
              onClick={handlePresetSearch}
              disabled={!originMarker || isSearching}
              data-tip="walk.presetSearch"
            >
              {isSearching ? 'Kontrollerar...' : `Kontrollera för ${activePreset.label}`}
            </button>

            {presetResults && (
              <div className={`walk-missing${missingResults.length === 0 ? ' is-clear' : ''}`}>
                {missingResults.length === 0 ? (
                  <strong>Allt finns inom riktavstånd.</strong>
                ) : (
                  <>
                    <strong>Saknas inom gångavstånd ({missingResults.length} av {presetResults.length}):</strong>
                    <ul>
                      {missingResults.map((r) => (
                        <li key={r.need.id} className={`is-${r.status}`}>
                          <span className="walk-missing-label">{r.need.label}</span>
                          <span className="walk-missing-target">riktvärde {targetLabel(r.need)}</span>
                          <span className="walk-missing-detail">{describeResult(r)}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <p className="walk-missing-note">
                  Bara platser inom det blå området (10 minuters promenad) visas och räknas.
                  Grönt på kartan = inom riktavstånd, gult = inom området men längre bort än riktvärdet.
                  Klicka på ett namn för att se gångvägen.
                </p>
              </div>
            )}

            {activePreset.gaps && <p className="walk-preset-gaps">{activePreset.gaps}</p>}
          </div>
        )}
      </section>

      <h2 className="walk-manual-head">Egen sökning</h2>
      <div className="options-container">
        <PlaceTypeSelector
          selectedTypes={selectedTypes}
          onChange={setSelectedTypes}
        />

        <div className="buttons-container">
          <button
            id="searchButton"
            onClick={handleSearch}
            disabled={!originMarker || isSearching}
            data-tip="walk.search"
          >
            {isSearching ? 'Söker...' : 'Sök platser/service inom 10 min promenad'}
          </button>

          {Object.keys(typeCounts).length > 0 && (
            <div id="type-counts">
              <strong>Platser inom 10 min promenad:</strong><br />
              {Object.values(typeCounts).map((text, index) => (
                <div key={index}>{text}</div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div id="status" className={statusIsError || status.includes('Error') || status.includes('Please') ? 'error' : 'success'}>
        {status}
      </div>

      {isSearching && (
        <div id="loading-popup">
          <div className="spinner"></div>
          <p>Söker...</p>
        </div>
      )}
      </div>
    </>
  );
};

// Set up global ref for WalkingMap to call search
window.walkingMapRef = {};

export default WalkingDistanceCalculator;
